/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Button, Description, Input, Label, Sidebar } from "@nordcloud/gnui";
import {
  EnvironmentType,
  useUpdateEnvironmentMutation,
} from "~/generated/graphql";
import { Fieldset, ContactsInput, LoaderWrap } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { EnvironmentSchema } from "../ApplicationDetails/validators";
import { SwitchEnvType } from "./SwitchEnvType";

type FormData = {
  name: string;
  nid: string;
  description: string;
  type: EnvironmentType;
  contactIds: string[];
};

type Props = {
  initialValues: FormData;
  isEditMode?: boolean;
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
};

export function EnvironmentSidebar({
  initialValues,
  isSidebarOpen,
  setIsSidebarOpen,
  isLoading,
}: Props) {
  const [typeValue, setTypeValue] = React.useState<string>("");
  const [contactId, setContactId] = React.useState<string>("");

  const {
    register,
    handleSubmit,
    setValue,

    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(EnvironmentSchema),
  });

  const [updateEnvironment, { loading: loadingUpdate }] =
    useUpdateEnvironmentMutation();

  const onUpdate = async (values: FormData) => {
    try {
      const { nid, ...restValues } = values;
      await updateEnvironment({
        variables: {
          nid,
          input: {
            ...restValues,
          },
        },
      });

      setIsSidebarOpen(false);
      showSuccess(generateActionSuccessText(`Environment `)()("updated")());
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  React.useEffect(() => {
    if (initialValues != null) {
      setValue("type", initialValues.type);
      setValue("nid", initialValues.nid);
      setTypeValue(initialValues.type);
      setValue("contactIds", initialValues.contactIds);
      setContactId(initialValues.contactIds[0]);
    }
  }, [initialValues, setValue]);

  React.useEffect(() => {
    register("type");
    register("contactIds");
  }, [register]);

  const showLoader = isLoading || loadingUpdate;

  return (
    <Sidebar
      title="Edit Environment"
      isOpen={isSidebarOpen}
      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
    >
      <LoaderWrap loading={showLoader} inContent>
        <form onSubmit={handleSubmit(onUpdate)}>
          <input type="hidden" {...register("nid")} />
          <Fieldset>
            <Label name="Environment Name" required />
            <Input
              type="text"
              {...register("name", { required: true })}
              defaultValue={initialValues?.name ?? ""}
              placeholder="Environment Name"
            />
            {errors.name && <Description>Name is required.</Description>}
          </Fieldset>
          <Fieldset>
            <Label name="Select Environment Type" required />
            <SwitchEnvType
              value={typeValue}
              onChange={(newType) => {
                setValue("type", newType);
                setTypeValue(newType);
              }}
            />
            {errors.type && <Description>Type is required.</Description>}
          </Fieldset>
          <Fieldset>
            <Label name="Contact Person" />
            <ContactsInput
              placeholder="Contact Person"
              value={[contactId]}
              status={errors.contactIds && "error"}
              onChange={(id) => {
                try {
                  setContactId(id);
                  setValue("contactIds", [id]);
                  showSuccess(SUCCESS_TEXT.contactPersonAdded);
                } catch {
                  showError(ERROR_TEXT.default);
                }
              }}
            />
          </Fieldset>
          <Fieldset>
            <Label name="Environment Description" />
            <Input
              type="text"
              {...register("description")}
              defaultValue={initialValues?.description ?? ""}
              placeholder="Environment Description"
            />
          </Fieldset>
          <Button type="submit">Update Environment</Button>
        </form>
      </LoaderWrap>
    </Sidebar>
  );
}
